/**
 * Domain shapes shared by the backend and the admin panel.
 *
 * These are storage-agnostic on purpose: no `_id`, no Mongoose types, nothing
 * that ties the business logic to a particular database. The data layer maps
 * its own documents to these shapes at the boundary.
 */

/** Bale (Telegram-compatible) numeric user id. */
export type BaleUserId = number;
/** Bale (Telegram-compatible) username. */
export type BaleUsername = string;
/* -------------------------------------------------------------------------- */
/* Session / FSM                                                              */
/* -------------------------------------------------------------------------- */

export const SESSION_STATES = [
  'IDLE',
  'MAIN_MENU',
  'WAITING_FOR_AM',
  'IN_LIVE_CHAT',
  'ADMIN_PANEL',
] as const;

export type SessionState = (typeof SESSION_STATES)[number];

export interface UserSession {
  userId: BaleUserId;
  currentState: SessionState;
  assignedManagerId?: BaleUserId;
  /** Folder ids from the root down to the menu level the user is looking at. */
  menuPath?: string[];
  lastActive: Date;
}

/* -------------------------------------------------------------------------- */
/* Dynamic bot configuration (edited from the admin panel)                    */
/* -------------------------------------------------------------------------- */

export interface BotMenuToggles {
  accountManagerEnabled: boolean;
  liveChatEnabled: boolean;
}
export interface MenuButton {
  text: string;
  id?: string;
  callback_data?: string;
  url?: string;
  /**
   * Children of a folder button, as rows. A flat list is still accepted for
   * layouts saved before rows existed and reads as one button per row.
   */
  submenu?: MenuButton[][] | MenuButton[];
}

export type CustomKeyboardLayout = MenuButton[][];
export interface BotConfiguration {
  /** Abstracted id — never Mongo's `_id`. */
  id: string;
  liveChatUrl: string;
  isRoundRobinActive: boolean;
  /** Round-robin pool of Bale usernames. */
  activeAccountManagers: BaleUsername[];
  fallbackMessage: string;
  welcomeMessage: string;
  menu: BotMenuToggles;
  customMenu?: CustomKeyboardLayout;
  /** ISO-8601 timestamp of the last successful update. */
  updatedAt: string;
}

/* -------------------------------------------------------------------------- */
/* Admin users / access control                                               */
/* -------------------------------------------------------------------------- */

export const USER_ROLES = ['admin', 'viewer'] as const;

export type UserRole = (typeof USER_ROLES)[number];

export const PERMISSIONS = [
  'settings:read',
  'settings:write',
  'analytics:read',
  'users:manage',
] as const;

export type Permission = (typeof PERMISSIONS)[number];

/** Default grants per role; a user may override them with explicit grants. */
export const ROLE_PERMISSIONS: Record<UserRole, readonly Permission[]> = {
  admin: ['settings:read', 'settings:write', 'analytics:read', 'users:manage'],
  viewer: ['analytics:read'],
};

/** An admin-panel account. The password hash never leaves the data layer. */
export interface AdminUser {
  id: string;
  username: string;
  role: UserRole;
  /** Explicit grants; when absent the role's defaults apply. */
  permissions?: Permission[];
  /** True for the account seeded on first boot, which must be replaced. */
  isDefault?: boolean;
  disabled?: boolean;
  createdAt: Date;
}

/* -------------------------------------------------------------------------- */
/* Events / analytics                                                         */
/* -------------------------------------------------------------------------- */

export const BOT_EVENT_TYPES = [
  'COMMAND_START',
  'MENU_SELECTED',
  'ROUTED_TO_AM',
  'ROUTED_TO_CHAT',
  'SESSION_ENDED',
  'FALLBACK',
  'ERROR',
] as const;

export type BotEventType = (typeof BOT_EVENT_TYPES)[number];

export interface BotEvent {
  id?: string;
  userId: BaleUserId;
  eventType: BotEventType;
  payload?: Record<string, unknown>;
  timestamp: Date;
}

export interface DateRange {
  startDate: Date;
  endDate: Date;
}
